import React, { useState, useEffect } from 'react'
import { format } from 'date-fns'
import { ru } from 'date-fns/locale'
import { apiGet } from '../utils/api'

interface TournamentControlData {
  tournamentId: string
  tournamentName: string
  clubName?: string
  startDate: string
  endDate?: string
  status: string
  days: Array<{
    date: string
    hours: number
    ballsConsumption: number
    ballsReturn: number
    waterConsumption: number
    waterReturn: number
    courtRentalAmount: number
    errors: string[]
  }>
  totals: {
    totalHours: number
    totalBallsConsumption: number
    totalBallsReturn: number
    totalWaterConsumption: number
    totalWaterReturn: number
    totalCourtRentalAmount: number
    totalErrors: number
  }
}

interface TournamentControlResponse {
  success: boolean
  data: TournamentControlData[]
  summary: {
    totalTournaments: number
    totalErrors: number
    tournamentsWithErrors: number
  }
}

const TournamentControlReport: React.FC = () => {
  const [data, setData] = useState<TournamentControlData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState({
    from: '',
    to: '',
    tournamentId: ''
  })
  const [expandedTournaments, setExpandedTournaments] = useState<Set<string>>(new Set())
  const [showOnlyWithErrors, setShowOnlyWithErrors] = useState(false)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      const params: Record<string, string> = {}
      
      if (filters.from) params.from = filters.from
      if (filters.to) params.to = filters.to
      if (filters.tournamentId) params.tournamentId = filters.tournamentId

      const result = await apiGet<TournamentControlResponse>('/reports/tournaments-control', params)
      setData(result.data)
      setError(null)
    } catch (err: any) {
      setError(err.message || 'Ошибка загрузки данных')
      console.error('Ошибка:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }

  const handleApplyFilters = () => {
    fetchData()
  }

  const toggleTournament = (tournamentId: string) => {
    const newExpanded = new Set(expandedTournaments)
    if (newExpanded.has(tournamentId)) {
      newExpanded.delete(tournamentId)
    } else {
      newExpanded.add(tournamentId)
    }
    setExpandedTournaments(newExpanded)
  }

  const filteredData = showOnlyWithErrors 
    ? data.filter(tournament => tournament.totals.totalErrors > 0)
    : data

  const formatDate = (dateStr: string) => {
    return format(new Date(dateStr), 'dd.MM.yyyy', { locale: ru })
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'MDL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PLANNED': return 'bg-blue-100 text-blue-800'
      case 'ACTIVE': return 'bg-green-100 text-green-800'
      case 'COMPLETED': return 'bg-gray-100 text-gray-800'
      case 'CANCELLED': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PLANNED': return 'Запланирован'
      case 'ACTIVE': return 'Активный'
      case 'COMPLETED': return 'Завершен'
      case 'CANCELLED': return 'Отменен'
      default: return status
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Ошибка</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Контроль турниров</h1>
        
        {/* Фильтры */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Дата с
            </label>
            <input
              type="date"
              value={filters.from}
              onChange={(e) => handleFilterChange('from', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Дата по
            </label>
            <input
              type="date"
              value={filters.to}
              onChange={(e) => handleFilterChange('to', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Турнир
            </label>
            <select
              value={filters.tournamentId}
              onChange={(e) => handleFilterChange('tournamentId', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Все турниры</option>
              {data.map(tournament => (
                <option key={tournament.tournamentId} value={tournament.tournamentId}>
                  {tournament.tournamentName}
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex items-end">
            <button
              onClick={handleApplyFilters}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Применить
            </button>
          </div>
        </div>

        {/* Переключатель */}
        <div className="flex items-center space-x-4 mb-6">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={showOnlyWithErrors}
              onChange={(e) => setShowOnlyWithErrors(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="ml-2 text-sm text-gray-700">Показать только турниры с ошибками</span>
          </label>
        </div>

        {/* Сводка */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-sm font-medium text-blue-600">Всего турниров</div>
            <div className="text-2xl font-bold text-blue-900">{data.length}</div>
          </div>
          <div className="bg-red-50 p-4 rounded-lg">
            <div className="text-sm font-medium text-red-600">Турниры с ошибками</div>
            <div className="text-2xl font-bold text-red-900">
              {data.filter(t => t.totals.totalErrors > 0).length}
            </div>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <div className="text-sm font-medium text-yellow-600">Всего ошибок</div>
            <div className="text-2xl font-bold text-yellow-900">
              {data.reduce((sum, t) => sum + t.totals.totalErrors, 0)}
            </div>
          </div>
        </div>
      </div>

      {/* Список турниров */}
      <div className="space-y-4">
        {filteredData.map((tournament) => (
          <div key={tournament.tournamentId} className="bg-white shadow rounded-lg">
            {/* Заголовок турнира */}
            <div 
              className="p-6 cursor-pointer hover:bg-gray-50"
              onClick={() => toggleTournament(tournament.tournamentId)}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {tournament.tournamentName}
                    </h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(tournament.status)}`}>
                      {getStatusLabel(tournament.status)}
                    </span>
                    {tournament.totals.totalErrors > 0 && (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                        {tournament.totals.totalErrors} ошибок
                      </span>
                    )}
                  </div>
                  {tournament.clubName && (
                    <p className="text-sm text-gray-600 mt-1">{tournament.clubName}</p>
                  )}
                  <p className="text-sm text-gray-500 mt-1">
                    {formatDate(tournament.startDate)} - {tournament.endDate ? formatDate(tournament.endDate) : 'не завершен'}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-600">Итого часов: {tournament.totals.totalHours}</div>
                </div>
                <div className="ml-4">
                  <svg
                    className={`w-5 h-5 transform transition-transform ${
                      expandedTournaments.has(tournament.tournamentId) ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Детали турнира */}
            {expandedTournaments.has(tournament.tournamentId) && (
              <div className="border-t border-gray-200">
                {/* Итоги турнира */}
                <div className="p-4 bg-gray-50">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Итоги по турниру</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <div className="text-gray-600">Часы аренды</div>
                      <div className="font-semibold">{tournament.totals.totalHours}</div>
                    </div>
                    <div>
                      <div className="text-gray-600">Расход мячей</div>
                      <div className="font-semibold">{tournament.totals.totalBallsConsumption}</div>
                    </div>
                    <div>
                      <div className="text-gray-600">Возврат мячей</div>
                      <div className="font-semibold">{tournament.totals.totalBallsReturn}</div>
                    </div>
                    <div>
                      <div className="text-gray-600">Расход воды</div>
                      <div className="font-semibold">{tournament.totals.totalWaterConsumption}</div>
                    </div>
                  </div>
                </div>

                {/* Дни турнира */}
                <div className="p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Детали по дням</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Дата
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Часы
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Мячи (расход)
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Мячи (возврат)
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Вода (расход)
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Ошибки
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {tournament.days
                          .filter(day => day.hours > 0 || day.ballsConsumption > 0 || day.ballsReturn > 0 || day.waterConsumption > 0 || day.errors.length > 0)
                          .map((day, index) => (
                          <tr key={index} className={day.errors.length > 0 ? 'bg-red-50' : ''}>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                              {formatDate(day.date)}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                              {day.hours}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                              {day.ballsConsumption}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                              {day.ballsReturn}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                              {day.waterConsumption}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm">
                              {day.errors.length > 0 ? (
                                <div className="space-y-1">
                                  {day.errors.map((error, errorIndex) => (
                                    <div key={errorIndex} className="text-red-600 text-xs">
                                      {error}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-green-600 text-xs">✓ Нет ошибок</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {filteredData.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg">Нет данных для отображения</div>
          <div className="text-gray-400 text-sm mt-2">
            Попробуйте изменить фильтры или добавить данные
          </div>
        </div>
      )}
    </div>
  )
}

export default TournamentControlReport
